import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { Invitation } from '@auth/models/invitation';
import { User } from '@auth/models/user';
import { RegistrationStoreService } from '@auth/store/services/registration-store.service';
import { UserStoreService } from '@auth/store/services/user-store.service';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { ToastService } from '@core-services/toast.service';
import { TranslationLoaderService } from '@core-services/translation-loader.service';

export const registrationGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const routeService = inject(RouteService);
    const userStoreService = inject(UserStoreService);
    const toastService = inject(ToastService);
    const translationLoaderService = inject(TranslationLoaderService);

    translationLoaderService.loadTranslations({
        lang: 'en',
        data: {
            REGISTRATION_GUARD: {
                WRONG_USER: 'Cannot proceed with another workspace addition because it is intended for another user.'
            }
        }
    });

    inject(RegistrationStoreService).loadInvitation(route.params.code as string);

    return combineLatest([userStoreService.getUserInvitation(), userStoreService.getUser()]).pipe(
        take(1),
        switchMap(([invitation, user]) => {
            const forbidAction = (): Observable<boolean> => {
                routeService.navigate(RpcRoute.Portfolio).catch(() => { });

                return of(false);
            };

            const hasLoggedInUserData = user?.loginData != null;
            const isSimpleRegistration = !invitation.isWorkspaceAddition;

            if (hasLoggedInUserData && isSimpleRegistration) {
                return forbidAction();
            }

            if (!hasLoggedInUserData || isInvitationForLoggedInUser(user, invitation)) {
                return of(true);
            }

            toastService.showClientError('REGISTRATION_GUARD.WRONG_USER');

            return forbidAction();
        })
    );
};

const isInvitationForLoggedInUser = (user: User, invitation: Invitation): boolean => {
    return user != null && invitation != null && invitation.isWorkspaceAddition && invitation.customer.email === user.customerUserName;
};